import React, { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import "./App.css";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import ChaptersA from "./components/ChaptersA";
import ChaptersB from "./components/ChaptersB";
import ChaptersC from "./components/ChaptersC";
import ChaptersD from "./components/ChaptersD";
import ChaptersE from "./components/ChaptersE";
import AckPage from "./components/AckPage";
import AuditMemo from "./components/AuditMemo";
import PreclearForm from "./components/PreclearForm"; // eslint-disable-line no-unused-vars -- retired v1.8-D, kept for archive reference
import SopAssistant from "./components/SopAssistant"; // eslint-disable-line no-unused-vars -- disabled pending counsel review (feature-flagged)
import AssistantWidget from "./components/AssistantWidget"; // eslint-disable-line no-unused-vars -- disabled pending counsel review
import CapRegister from "./components/CapRegister";
import ScreeningLog from "./components/ScreeningLog";
import DeadlineCalendar from "./components/DeadlineCalendar";
import RemindersBar from "./components/RemindersBar";
import OverdueSpotlight from "./components/OverdueSpotlight";
import CasePipeline from "./components/CasePipeline";
import CredentialDashboard from "./components/CredentialDashboard";
import LenderReport from "./components/LenderReport";
import KpiDashboard from "./components/KpiDashboard";
import IncidentLog from "./components/IncidentLog";
import TrainingLog from "./components/TrainingLog";
import OverpaymentLog from "./components/OverpaymentLog";
import BaaInventory from "./components/BaaInventory";
import EmailCenter from "./components/EmailCenter";
import FeedbackRegister from "./components/FeedbackRegister";
import VendorReports from "./components/VendorReports";
import CommitteePack from "./components/CommitteePack";
import AuditTrail from "./components/AuditTrail";
import CaseTimeline from "./components/CaseTimeline";
import TabletopLog from "./components/TabletopLog";
import DisciplineLog from "./components/DisciplineLog";
import ReadCompliance from "./components/ReadCompliance";
import MasterChecklist from "./components/MasterChecklist";
import MonthlyCaseFile from "./components/MonthlyCaseFile";
import VersionHistory from "./components/VersionHistory";
import CadenceBar from "./components/CadenceBar";
import CadenceBoard from "./components/CadenceBoard";
import LockScreen from "./components/LockScreen";
import NavFooter from "./components/NavFooter";

const TOKEN_KEY = "ech_token";
const USER_KEY = "ech_user";
// Session cookie shared across www/non-www — localStorage does not cross subdomains
const cookieDomain = () => {
  const h = window.location.hostname;
  if (!h.includes(".") || /^[\d.]+$/.test(h)) return "";
  return `; domain=.${h.replace(/^www\./, "")}`;
};
const setTokenCookie = (t) => {
  document.cookie = `${TOKEN_KEY}=${t}; path=/${cookieDomain()}; max-age=2592000; SameSite=Lax${window.location.protocol === "https:" ? "; Secure" : ""}`;
};
const getTokenCookie = () => (document.cookie.match(/(?:^|;\s*)ech_token=([^;]+)/) || [])[1] || "";
const clearTokenCookie = () => {
  document.cookie = `${TOKEN_KEY}=; path=/${cookieDomain()}; max-age=0`;
  document.cookie = `${TOKEN_KEY}=; path=/; max-age=0`;
};
const savedToken = localStorage.getItem(TOKEN_KEY) || getTokenCookie();
if (savedToken) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${savedToken}`;
  localStorage.setItem(TOKEN_KEY, savedToken);
  setTokenCookie(savedToken);
}
const savedUser = (() => {
  try { return JSON.parse(localStorage.getItem(USER_KEY)); } catch { return null; }
})();

const MANUAL_VERSION = "1.8-D";

const NAV_GROUPS = [
  { grp: "Start", items: [{ id: "c0", n: "\u25c8", label: "Cover & How to Use" }, { id: "ver", n: "\u0394", label: "Version History" }] },
  {
    grp: "Foundation",
    items: [
      { id: "c1", n: "01", label: "Company & Governance" },
      { id: "c2", n: "02", label: "Compliance Program" },
    ],
  },
  {
    grp: "Legal & Integrity",
    items: [
      { id: "c3", n: "03", label: "Regulatory Framework" },
      { id: "c4", n: "04", label: "FWA & Claims Integrity" },
      { id: "c5", n: "05", label: "Exclusion Screening" },
      { id: "c6", n: "06", label: "HIPAA Privacy & Security" },
    ],
  },
  {
    grp: "People",
    items: [
      { id: "c7", n: "07", label: "Credentialing & Enrollment" },
      { id: "c8", n: "08", label: "Recruiting & Classification" },
      { id: "c9", n: "09", label: "Workforce Safety (OSHA)" },
    ],
  },
  {
    grp: "Operations",
    items: [
      { id: "c10", n: "10", label: "Facility & Clinical Engagement" },
      { id: "c11", n: "11", label: "Case Intake & Eligibility" },
      { id: "c12", n: "12", label: "Documentation & Evidence" },
      { id: "c13", n: "13", label: "Billing, RCM & Vendor Oversight" },
    ],
  },
  {
    grp: "Money & Measurement",
    items: [
      { id: "c14", n: "14", label: "Reporting Framework" },
      { id: "c15", n: "15", label: "Finance & Payroll" },
      { id: "c16", n: "16", label: "Audit & QA" },
    ],
  },
  {
    grp: "Outward-Facing",
    items: [
      { id: "c17", n: "17", label: "Patient Communications" },
      { id: "c18", n: "18", label: "Government Inquiries" },
      { id: "c19", n: "19", label: "Continuity & Incidents" },
    ],
  },
  {
    grp: "Program Instruments",
    items: [
      { id: "c21", n: "20", label: "Monthly Review (MOCR)" },
      { id: "c20", n: "21", label: "Appendices & Crosswalk" },
      { id: "c22", n: "!", label: "[VERIFY] Register" },
    ],
  },
  {
    grp: "Working Records — not printed",
    items: [
      { id: "mcf", n: "MF", label: "Monthly Case File" },
      { id: "cad", n: "31", label: "Monthly Cadence" },
      { id: "pipe", n: "\u25b8", label: "Case Pipeline (read-only)" },
      { id: "ctl", n: "\u2192", label: "Case Timeline (read-only)" },
      { id: "cred", n: "\u2713", label: "Credential Dashboard" },
      { id: "ack", n: "\u270e", label: "Acknowledgments (SOP-1.1)" },
      { id: "rdc", n: "\u2713", label: "Read Compliance" },
      { id: "fb", n: "FB", label: "Change Requests (Review)" },
      { id: "a8", n: "A8", label: "Exclusion Screening (A-8)" },
      { id: "caps", n: "\u2699", label: "CAP Register (SOP-2.1)" },
      { id: "inc", n: "!", label: "Incident Log (SOP-19)" },
      { id: "trn", n: "T", label: "Training Log (SOP-2.4)" },
      { id: "disc", n: "D", label: "Discipline Log (A-18)" },
      { id: "ttx", n: "TT", label: "Tabletop Exercises" },
      { id: "op", n: "60", label: "Overpayment 60-Day Log" },
      { id: "baa", n: "B", label: "BAA Inventory (SOP-6.3)" },
      { id: "cal", n: "\u23f1", label: "Statutory Windows (read-only)" },
      { id: "rpt", n: "15", label: "Lender Report (SOP-14.1)" },
      { id: "vrep", n: "13", label: "Alteva Reports (SOP-13.3)" },
      { id: "kpi", n: "%", label: "KPI Dashboard (SOP-14.2)" },
      { id: "pack", n: "Q", label: "Committee Pack (SOP-2.2)" },
      { id: "trail", n: "\u2261", label: "Audit Trail" },
      { id: "mail", n: "@", label: "Email Center" },
      { id: "audit", n: "\u00a7", label: "Internal Audit Memo (frozen)" },
    ],
  },
];

// Retired/relocated section ids → where they live now (old links must not 404)
const ID_ALIASES = { a4: "c11", mcl: "c11", ai: "c0" };

const MANUAL_IDS = NAV_GROUPS.slice(0, 8).flatMap((g) => g.items.map((i) => i.id));
const VALID_IDS = NAV_GROUPS.flatMap((g) => g.items.map((i) => i.id));
const STORE_KEY = "ech_reader";

const loadReader = () => {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY)) || { name: "", read: {} };
  } catch {
    return { name: "", read: {} };
  }
};

function ChapterConfirmBar({ active, readMap, onConfirm, user }) {
  if (!MANUAL_IDS.includes(active)) return null;
  const done = readMap[active];
  const stale = done && done.manual_version !== MANUAL_VERSION;
  if (done && !stale) {
    return (
      <div className="chapter-confirm done" data-testid="chapter-confirm-bar">
        <span data-testid="chapter-confirmed">
          ✓ Read &amp; understood — confirmed by {user?.name || "you"} on {new Date(done.confirmed_at).toLocaleDateString("en-US")} (v{done.manual_version}) · recorded to your compliance file (SOP-1.1)
        </span>
      </div>
    );
  }
  return (
    <div className={`chapter-confirm${stale ? " stale" : ""}`} data-testid="chapter-confirm-bar">
      {stale ? (
        <>
          <span data-testid="chapter-stale-note">
            You confirmed this section under {done.manual_version ? `v${done.manual_version}` : "an earlier version"} on {new Date(done.confirmed_at).toLocaleDateString("en-US")} — the manual has since been updated to v{MANUAL_VERSION}. SOP-1.1 requires re-confirmation of updated content:
          </span>
          <button className="chapter-confirm-btn" onClick={() => onConfirm(active)} data-testid="chapter-reconfirm-btn">
            I have re-read &amp; understood the updated section
          </button>
        </>
      ) : (
        <>
          <span>End of this section. Compliance requires your explicit confirmation — it is recorded under your account:</span>
          <button className="chapter-confirm-btn" onClick={() => onConfirm(active)} data-testid="chapter-confirm-btn">
            I have fully read &amp; understood this section
          </button>
        </>
      )}
    </div>
  );
}

function App() {
  const [auth, setAuth] = useState({ checked: !savedToken, token: savedToken || "", user: savedUser });
  const [active, setActive] = useState(() => {
    const h = window.location.hash.slice(1);
    const id = ID_ALIASES[h] || h;
    return VALID_IDS.includes(id) ? id : "c0";
  });
  const [reader, setReader] = useState(loadReader);
  const dwellRef = useRef(null); // eslint-disable-line no-unused-vars

  useEffect(() => {
    if (savedToken) {
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { timeout: 10000 })
        .then((r) => {
          localStorage.setItem(USER_KEY, JSON.stringify(r.data));
          setAuth((p) => ({ ...p, checked: true, user: r.data }));
        })
        .catch((err) => {
          if (err.response) {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
            clearTokenCookie();
            delete axios.defaults.headers.common["Authorization"];
            setAuth({ checked: true, token: "", user: null });
          } else {
            setAuth((p) => ({ ...p, checked: true }));
          }
        });
    }
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 401 && !(err.config?.url || "").includes("/api/auth/")) {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          clearTokenCookie();
          delete axios.defaults.headers.common["Authorization"];
          setAuth((p) => ({ ...p, token: "", user: null, checked: true }));
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const onAuthed = useCallback((token, user) => {
    localStorage.setItem(TOKEN_KEY, token);
    setTokenCookie(token);
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setAuth((p) => ({ ...p, token, user: user || p.user, checked: true }));
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    clearTokenCookie();
    delete axios.defaults.headers.common["Authorization"];
    setAuth((p) => ({ ...p, token: "", user: null }));
  }, []);

  useEffect(() => {
    localStorage.setItem(STORE_KEY, JSON.stringify(reader));
  }, [reader]);

  // Reading progress: explicit confirmation only (SOP-1.1) — per-user, stored server-side
  const [readMap, setReadMap] = useState({});
  useEffect(() => {
    if (auth.token) {
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/chapter-reads`)
        .then((r) => setReadMap(r.data))
        .catch(() => {});
    } else {
      setReadMap({});
    }
  }, [auth.token]);

  const confirmRead = useCallback(async (id) => {
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/chapter-reads`, { chapter_id: id });
      setReadMap((p) => ({ ...p, [id]: { confirmed_at: res.data.confirmed_at, manual_version: res.data.manual_version } }));
      toast.success("Confirmation recorded to your compliance file (SOP-1.1)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record your confirmation — check your connection and try again");
    }
  }, []);

  const show = useCallback((id) => {
    id = ID_ALIASES[id] || id;
    if (!VALID_IDS.includes(id)) id = "c0";
    setActive(id);
    window.scrollTo({ top: 0 });
    window.history.replaceState(null, "", "#" + id);
  }, []);

  useEffect(() => {
    const h = () => show("ctl");
    const nav = (e) => { if (e.detail) show(e.detail); };
    window.addEventListener("ech:open-timeline", h);
    window.addEventListener("ech:navigate", nav);
    return () => { window.removeEventListener("ech:open-timeline", h); window.removeEventListener("ech:navigate", nav); };
  }, [show]);

  const onSearch = useCallback((e) => {
    const q = e.target.value.trim().toLowerCase();
    const items = document.querySelectorAll(".sop,.pol,.rule");
    items.forEach((el) => {
      el.style.outline = "";
    });
    if (q.length < 3) return;
    let first = null;
    items.forEach((el) => {
      if (el.textContent.toLowerCase().indexOf(q) !== -1) {
        el.style.outline = "2px solid #2E7D6E";
        if (!first) first = el;
      }
    });
    if (first) {
      const chap = first.closest(".chapter");
      if (chap) {
        setActive(chap.id);
        window.history.replaceState(null, "", "#" + chap.id);
        setTimeout(() => first.scrollIntoView({ block: "center" }), 30);
      }
    }
  }, []);

  const readCount = MANUAL_IDS.filter((id) => readMap[id]).length;

  const exportCompletion = useCallback(() => {
    const label = (id) => {
      for (const g of NAV_GROUPS) {
        const it = g.items.find((i) => i.id === id);
        if (it) return `${it.n} ${it.label}`;
      }
      return id;
    };
    const done = MANUAL_IDS.filter((id) => readMap[id]);
    const remaining = MANUAL_IDS.filter((id) => !readMap[id]);
    const lines = [
      "ECH OPERATIONS, COMPLIANCE & SOP MANUAL — READING COMPLETION RECORD",
      `Manual version: v${MANUAL_VERSION}`,
      `Reader: ${reader.name || auth.user?.name || "(name not set)"} (account: ${auth.user?.email || "—"})`,
      `Generated: ${new Date().toLocaleString("en-US")}`,
      `Progress: ${done.length} of ${MANUAL_IDS.length} chapters confirmed read & understood`,
      "",
      "NOTE — This record is a training convenience, not the compliance record.",
      "The audit-grade evidence remains the signed acknowledgment under ECH-SOP-1.1;",
      "send this record to the Compliance Officer to file with your acknowledgment.",
      "",
      "CHAPTERS CONFIRMED (read & understood):",
      ...done.map((id) => `  [x] ${label(id)} — ${new Date(readMap[id].confirmed_at).toLocaleDateString("en-US")} (v${readMap[id].manual_version || "?"})`),
      "",
      "CHAPTERS REMAINING:",
      ...(remaining.length ? remaining.map((id) => `  [ ] ${label(id)}`) : ["  (none — complete)"]),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `ECH_completion_record_${(reader.name || "reader").replace(/\s+/g, "_")}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [reader, readMap, auth.user]);

  if (!auth.checked) {
    return <div className="lock-screen"><div className="lock-card"><div className="lock-brand">ECH <span>/ loading…</span></div></div></div>;
  }
  if (!auth.token) {
    return (
      <>
        <Toaster position="bottom-right" richColors />
        <LockScreen onAuthed={onAuthed} />
      </>
    );
  }

  return (
    <div className="App">
      <Toaster position="bottom-right" richColors />
      <div className="topbar">
        <div className="brand">
          ECH <span>/ Operations, Compliance &amp; SOP Manual</span>
        </div>
        <div className="meta">v1.8 DRAFT · 08.25.2026</div>
        <div className="searchbox">
          <input
            id="srch"
            type="text"
            placeholder="filter SOPs… (e.g. exclusion, IDR, pre-clear)"
            onChange={onSearch}
            data-testid="sop-search-input"
          />
        </div>
        <button className="printbtn" onClick={() => window.print()} data-testid="print-pdf-btn" title="Prints the full manual, cleanly paginated. Working records and the audit memo are excluded.">
          Print / PDF
        </button>
        <div className="conf">CONFIDENTIAL — INTERNAL</div>
      </div>
      <div className="layout">
        <nav id="nav">
          <div className="progress-panel" data-testid="progress-panel">
            <input
              className="pp-name"
              placeholder="Your name (for records)"
              value={reader.name}
              onChange={(e) => setReader((p) => ({ ...p, name: e.target.value }))}
              data-testid="reader-name-input"
            />
            <div className="pp-row">
              <span className="pp-count" data-testid="progress-count">{readCount}/{MANUAL_IDS.length} chapters read</span>
              <button className="pp-export" onClick={exportCompletion} data-testid="export-completion-btn">Export record</button>
            </div>
            <div className="pp-bar"><div className="pp-fill" style={{ width: `${(readCount / MANUAL_IDS.length) * 100}%` }} /></div>
          </div>
          {NAV_GROUPS.map((g) => (
            <React.Fragment key={g.grp}>
              <div className="grp">{g.grp}</div>
              {g.items.map((item) => (
                <a
                  key={item.id}
                  href={"#" + item.id}
                  className={active === item.id ? "active" : ""}
                  data-testid={`nav-${item.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    show(item.id);
                  }}
                >
                  <span className="n">{item.n}</span>
                  {item.label}
                  {readMap[item.id] && <span className="readmark" title={`Confirmed ${new Date(readMap[item.id].confirmed_at).toLocaleDateString("en-US")}${readMap[item.id].manual_version !== MANUAL_VERSION ? " — re-confirmation needed (manual updated)" : ""}`}>{readMap[item.id].manual_version === MANUAL_VERSION || !MANUAL_IDS.includes(item.id) ? "✓" : "↻"}</span>}
                </a>
              ))}
            </React.Fragment>
          ))}
          <NavFooter user={auth.user} onSignOut={signOut} onNewToken={onAuthed} />
        </nav>
        <main>
          <CadenceBar show={show} />
          <RemindersBar show={show} />
          <OverdueSpotlight active={active} show={show} />
          <ChaptersA active={active} />
          <VersionHistory active={active} />
          <ChaptersB active={active} />
          <ChaptersC active={active} />
          <ChaptersD active={active} />
          <ChaptersE active={active} />
          <AckPage active={active} />
          <ScreeningLog active={active} />
          <CapRegister active={active} />
          <DeadlineCalendar active={active} show={show} />
          <CasePipeline active={active} show={show} />
          <CredentialDashboard active={active} />
          <LenderReport active={active} />
          <KpiDashboard active={active} />
          <IncidentLog active={active} />
          <TrainingLog active={active} />
          <OverpaymentLog active={active} />
          <BaaInventory active={active} />
          <EmailCenter active={active} />
          <CommitteePack active={active} />
          <AuditTrail active={active} />
          <CaseTimeline active={active} show={show} />
          <FeedbackRegister active={active} />
          <TabletopLog active={active} />
          <DisciplineLog active={active} />
          <ReadCompliance active={active} />
          <MasterChecklist active={active} />
          <MonthlyCaseFile active={active} show={show} />
          <CadenceBoard active={active} show={show} />
          <VendorReports active={active} />
          <AuditMemo active={active} />
          <ChapterConfirmBar active={active} readMap={readMap} onConfirm={confirmRead} user={auth.user} />
        </main>
      </div>
    </div>
  );
}

export default App;
